import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const PermissionsContext = createContext();

const FALLBACK_ROLE_ALIASES = {
  viewer: 'client'
};

const FALLBACK_ROLE_DEFINITIONS = {
  admin: {
    key: 'admin',
    label: 'Administrador',
    level: 4,
    modulePermissions: {
      dashboard: { view: true, export: true },
      projects: { view: true, create: true, edit: true, delete: true, export: true },
      clients: { view: true, create: true, edit: true, delete: true, export: true },
      users: { view: true, create: true, edit: true, delete: true },
      tasks: { view: true, create: true, edit: true, delete: true, assign: true },
      documents: { view: true, upload: true, download: true, delete: true },
      expenseRequests: { view: true, create: true, edit: true, delete: true, approve: true, export: true },
      partnerQuotes: { view: true, reply: true, manage: true },
      billingDocuments: { view: true, create: true, edit: true, delete: true, approve: true },
      finance: { view: true, create: true, edit: true, delete: true, approve: true },
      fiscal: { view: true, manage: true, validate: true, stamp: false, cancel: false, certificates: true },
      quality: { view: true, export: true },
      reports: { view: true, generate: true, export: true },
      chat: { view: true, send: true, delete: true },
      notifications: { view: true, manage: true },
      settings: { view: true, edit: true },
      roles: { view: true, create: true, edit: true, delete: true },
      activityLog: { view: true, export: true },
      ndaAgreements: { view: true, create: true, edit: true, approve: true, send: true, revoke: true, archive: true, download: true, audit: true, manage: true }
    },
    billingDocumentScopes: { invoice: 'all', quote: 'all', purchase_order: 'all' }
  },
  manager: {
    key: 'manager',
    label: 'Manager',
    level: 3,
    modulePermissions: {
      dashboard: { view: true, export: true },
      projects: { view: true, create: true, edit: true, delete: false, export: true },
      clients: { view: true, create: true, edit: true, delete: false, export: true },
      users: { view: true, create: false, edit: false, delete: false },
      tasks: { view: true, create: true, edit: true, delete: true, assign: true },
      documents: { view: true, upload: true, download: true, delete: false },
      expenseRequests: { view: true, create: true, edit: true, delete: false, approve: true, export: true },
      partnerQuotes: { view: true, reply: true, manage: true },
      billingDocuments: { view: true, create: true, edit: true, delete: false, approve: true },
      finance: { view: true, create: true, edit: true, delete: false, approve: false },
      fiscal: { view: true, manage: false, validate: false, stamp: false, cancel: false, certificates: false },
      quality: { view: true, export: true },
      reports: { view: true, generate: true, export: true },
      chat: { view: true, send: true, delete: false },
      notifications: { view: true, manage: false },
      settings: { view: true, edit: false },
      roles: { view: false, create: false, edit: false, delete: false },
      activityLog: { view: false, export: false },
      ndaAgreements: { view: true, create: true, edit: true, approve: true, send: true, revoke: true, archive: true, download: true, audit: true, manage: false }
    },
    billingDocumentScopes: { invoice: 'own', quote: 'all', purchase_order: 'own' }
  },
  user: {
    key: 'user',
    label: 'Usuario',
    level: 2,
    modulePermissions: {
      dashboard: { view: true, export: false },
      projects: { view: true, create: false, edit: false, delete: false, export: false },
      clients: { view: true, create: false, edit: false, delete: false, export: false },
      users: { view: false, create: false, edit: false, delete: false },
      tasks: { view: true, create: true, edit: true, delete: false, assign: false },
      documents: { view: true, upload: true, download: true, delete: false },
      expenseRequests: { view: true, create: true, edit: true, delete: true, approve: false, export: false },
      partnerQuotes: { view: false, reply: false, manage: false },
      billingDocuments: { view: false, create: false, edit: false, delete: false, approve: false },
      finance: { view: false, create: false, edit: false, delete: false, approve: false },
      fiscal: { view: false, manage: false, validate: false, stamp: false, cancel: false, certificates: false },
      quality: { view: true, export: false },
      reports: { view: false, generate: false, export: false },
      chat: { view: true, send: true, delete: false },
      notifications: { view: true, manage: false },
      settings: { view: true, edit: false },
      roles: { view: false, create: false, edit: false, delete: false },
      activityLog: { view: false, export: false },
      ndaAgreements: { view: true, create: true, edit: true, approve: false, send: false, revoke: false, archive: true, download: true, audit: true, manage: false }
    },
    billingDocumentScopes: { invoice: 'own', quote: 'own', purchase_order: 'own' }
  },
  client: {
    key: 'client',
    label: 'Cliente',
    level: 1,
    modulePermissions: {
      dashboard: { view: false, export: false },
      projects: { view: true, create: false, edit: false, delete: false, export: false },
      clients: { view: false, create: false, edit: false, delete: false, export: false },
      users: { view: false, create: false, edit: false, delete: false },
      tasks: { view: true, create: false, edit: false, delete: false, assign: false },
      documents: { view: true, upload: false, download: true, delete: false },
      expenseRequests: { view: false, create: false, edit: false, delete: false, approve: false, export: false },
      partnerQuotes: { view: false, reply: false, manage: false },
      billingDocuments: { view: false, create: false, edit: false, delete: false, approve: false },
      finance: { view: false, create: false, edit: false, delete: false, approve: false },
      fiscal: { view: false, manage: false, validate: false, stamp: false, cancel: false, certificates: false },
      quality: { view: false, export: false },
      reports: { view: false, generate: false, export: false },
      chat: { view: true, send: true, delete: false },
      notifications: { view: true, manage: false },
      settings: { view: false, edit: false },
      roles: { view: false, create: false, edit: false, delete: false },
      activityLog: { view: false, export: false },
      ndaAgreements: { view: false, create: false, edit: false, approve: false, send: false, revoke: false, archive: false, download: false, audit: false, manage: false }
    },
    billingDocumentScopes: { invoice: 'none', quote: 'none', purchase_order: 'none' }
  }
};

const normalizeRole = (rawRole, roleDefinitions, roleAliases) => {
  const role = String(rawRole || '').trim().toLowerCase();
  if (!role) return 'user';
  if (roleDefinitions[role]) return role;

  const alias = roleAliases[role];
  if (alias && roleDefinitions[alias]) return alias;
  return 'user';
};

const buildRoleMapForUi = (roleDefinitions) => {
  return Object.entries(roleDefinitions).reduce((acc, [roleKey, definition]) => {
    acc[roleKey] = {
      name: definition?.label || roleKey,
      permissions: definition?.modulePermissions || {}
    };
    return acc;
  }, {});
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [serverRoleDefinitions, setServerRoleDefinitions] = useState(null);
  const [serverRoleAliases, setServerRoleAliases] = useState({});
  const [serverEffectivePermissions, setServerEffectivePermissions] = useState(null);
  const [serverEffectiveBillingDocumentScopes, setServerEffectiveBillingDocumentScopes] = useState(null);
  const [serverUserOverrides, setServerUserOverrides] = useState({});
  const [serverHasCustomOverrides, setServerHasCustomOverrides] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchRoleMatrix = async () => {
      if (!user) {
        if (mounted) {
          setServerRoleDefinitions(null);
          setServerRoleAliases({});
          setServerEffectivePermissions(null);
          setServerEffectiveBillingDocumentScopes(null);
          setServerUserOverrides({});
          setServerHasCustomOverrides(false);
        }
        return;
      }

      try {
        const response = await api.get('/roles/permissions');
        if (!mounted) return;

        const nextDefinitions = response?.data?.roleDefinitions;
        const nextAliases = response?.data?.acceptedAliases;
        const nextEffectivePermissions = response?.data?.currentUserEffectiveModulePermissions;
        const nextEffectiveBillingScopes = response?.data?.currentUserEffectiveBillingDocumentScopes;
        const nextOverrides = response?.data?.currentUserOverrides;
        const nextHasOverrides = response?.data?.currentUserHasCustomOverrides === true;

        setServerRoleDefinitions(
          nextDefinitions && typeof nextDefinitions === 'object'
            ? nextDefinitions
            : null
        );
        setServerRoleAliases(
          nextAliases && typeof nextAliases === 'object'
            ? nextAliases
            : {}
        );
        setServerEffectivePermissions(
          nextEffectivePermissions && typeof nextEffectivePermissions === 'object'
            ? nextEffectivePermissions
            : null
        );
        setServerEffectiveBillingDocumentScopes(
          nextEffectiveBillingScopes && typeof nextEffectiveBillingScopes === 'object'
            ? nextEffectiveBillingScopes
            : null
        );
        setServerUserOverrides(
          nextOverrides && typeof nextOverrides === 'object'
            ? nextOverrides
            : {}
        );
        setServerHasCustomOverrides(nextHasOverrides);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No se pudo cargar la matriz de permisos desde backend. Se usa fallback local.');
        }
        if (mounted) {
          setServerRoleDefinitions(null);
          setServerRoleAliases({});
          setServerEffectivePermissions(null);
          setServerEffectiveBillingDocumentScopes(null);
          setServerUserOverrides({});
          setServerHasCustomOverrides(false);
        }
      }
    };

    fetchRoleMatrix();

    return () => {
      mounted = false;
    };
  }, [user]);

  const roleDefinitions = useMemo(() => {
    if (serverRoleDefinitions && Object.keys(serverRoleDefinitions).length > 0) {
      return serverRoleDefinitions;
    }
    return FALLBACK_ROLE_DEFINITIONS;
  }, [serverRoleDefinitions]);

  const roleAliases = useMemo(() => {
    return {
      ...FALLBACK_ROLE_ALIASES,
      ...(serverRoleAliases || {})
    };
  }, [serverRoleAliases]);

  const currentRole = useMemo(() => {
    return normalizeRole(user?.role, roleDefinitions, roleAliases);
  }, [user, roleDefinitions, roleAliases]);

  const permissions = useMemo(() => {
    if (serverEffectivePermissions && typeof serverEffectivePermissions === 'object') {
      return serverEffectivePermissions;
    }
    return roleDefinitions[currentRole]?.modulePermissions || FALLBACK_ROLE_DEFINITIONS.user.modulePermissions;
  }, [currentRole, roleDefinitions, serverEffectivePermissions]);

  const billingDocumentScopes = useMemo(() => {
    if (serverEffectiveBillingDocumentScopes && typeof serverEffectiveBillingDocumentScopes === 'object') {
      return serverEffectiveBillingDocumentScopes;
    }
    return roleDefinitions[currentRole]?.billingDocumentScopes || FALLBACK_ROLE_DEFINITIONS.user.billingDocumentScopes;
  }, [currentRole, roleDefinitions, serverEffectiveBillingDocumentScopes]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 PermissionsContext actualizado:', {
        user: user?.email,
        role: user?.role,
        normalizedRole: currentRole,
        hasPermissions: !!permissions,
        hasCustomOverrides: serverHasCustomOverrides
      });
    }
  }, [currentRole, permissions, user, serverHasCustomOverrides]);

  const hasPermission = (module, action) => {
    return permissions[module]?.[action] === true;
  };

  const hasAnyPermission = (module, actions = []) => {
    return actions.some((action) => hasPermission(module, action));
  };

  const hasAllPermissions = (module, actions = []) => {
    return actions.every((action) => hasPermission(module, action));
  };

  const canViewModule = (module) => {
    return hasPermission(module, 'view');
  };

  const getBillingDocumentScope = (docType) => {
    const normalizedType = String(docType || '').toLowerCase().trim();
    const rawScope = billingDocumentScopes?.[normalizedType];
    if (rawScope === 'all' || rawScope === 'own' || rawScope === 'none') return rawScope;
    return 'own';
  };

  const canAccessBillingDocumentType = (docType) => getBillingDocumentScope(docType) !== 'none';

  const roles = useMemo(() => buildRoleMapForUi(roleDefinitions), [roleDefinitions]);

  const value = {
    currentRole,
    permissions,
    billingDocumentScopes,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewModule,
    getBillingDocumentScope,
    canAccessBillingDocumentType,
    roles,
    roleDefinitions,
    roleAliases,
    userPermissionOverrides: serverUserOverrides,
    hasCustomUserPermissionOverrides: serverHasCustomOverrides
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions debe usarse dentro de PermissionsProvider');
  }
  return context;
};
